import React from 'react';
import clsx from 'clsx';
import { Bot, User } from 'lucide-react';
import type { WizardFlow, WizardQuestion } from './types';

export interface PreviewMessage {
  role: 'bot' | 'user';
  text: string;
  isPlaceholder?: boolean;
}

function buildPreviewMessages(flow: WizardFlow): PreviewMessage[] {
  const msgs: PreviewMessage[] = [];

  // Greeting
  if (flow.greeting) {
    msgs.push({ role: 'bot', text: flow.greeting });
  }

  // Questions + sample answers
  for (const q of flow.questions) {
    msgs.push({ role: 'bot', text: q.prompt || '…' });
    if (q.type === 'choice' && q.options && q.options.length > 0) {
      msgs.push({ role: 'user', text: q.options[0].label, isPlaceholder: true });
    } else if (q.type === 'collect') {
      const placeholder: Record<string, string> = {
        name: 'John Smith',
        phone: '+1 555 0100',
        email: 'john@example.com',
        number: '42',
        text: 'Yes, sounds good!',
      };
      msgs.push({ role: 'user', text: placeholder[q.collectType ?? 'text'] ?? '…', isPlaceholder: true });
    }
  }

  // Closing message
  if (flow.closingMessage) {
    msgs.push({ role: 'bot', text: flow.closingMessage });
  }

  return msgs;
}

interface Props {
  flow: WizardFlow;
  highlightStep?: number;
}

export const WizardChatPreview: React.FC<Props> = ({ flow }) => {
  const messages = buildPreviewMessages(flow);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Phone shell header */}
      <div className="flex items-center gap-2.5 rounded-t-2xl border border-border bg-surface-alt px-4 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-success-soft text-success">
          <Bot size={15} />
        </div>
        <div>
          <div className="text-xs font-semibold text-text-primary">
            {flow.name || 'My bot'}
          </div>
          <div className="flex items-center gap-1 text-[10px] text-success">
            <span className="h-1.5 w-1.5 rounded-full bg-success inline-block" />
            Online
          </div>
        </div>
      </div>

      {/* Chat area */}
      <div
        className="flex-1 overflow-y-auto rounded-b-2xl border-x border-b border-border px-3 py-3 space-y-2"
        style={{ background: 'var(--color-bg)' }}
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 py-8 text-center">
            <Bot size={28} className="text-text-muted opacity-40" />
            <p className="text-xs text-text-muted">
              Your conversation preview will appear here as you build the flow.
            </p>
          </div>
        ) : (
          messages.map((msg, i) => {
            const isBot = msg.role === 'bot';
            return (
              <div
                key={i}
                className={clsx(
                  'flex flex-col',
                  isBot ? 'items-start' : 'items-end',
                )}
              >
                {isBot && (
                  <div className="mb-0.5 flex items-center gap-1 px-1">
                    <Bot size={9} className="text-primary opacity-60" />
                    <span className="text-[9px] font-medium text-primary opacity-60">Bot</span>
                  </div>
                )}
                {!isBot && (
                  <div className="mb-0.5 flex items-center justify-end gap-1 px-1">
                    <span className="text-[9px] font-medium text-text-muted">
                      {msg.isPlaceholder ? 'Sample reply' : 'Customer'}
                    </span>
                    <User size={9} className="text-text-muted" />
                  </div>
                )}
                <div
                  className={clsx(
                    'max-w-[80%] rounded-2xl px-3 py-2 text-[12px] leading-snug shadow-sm',
                    isBot
                      ? 'rounded-bl-sm bg-surface border border-border text-text-primary'
                      : msg.isPlaceholder
                      ? 'rounded-br-sm bg-surface-alt border border-dashed border-border text-text-muted italic'
                      : 'rounded-br-sm bg-primary-soft text-primary',
                  )}
                >
                  {msg.text}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Re-export types so this file is the consumer-facing API
export type { WizardFlow, WizardQuestion };
